/**
 * MONSTER TOWN — INTERACTION RUNNER. ARCHITECT-OWNED, FROZEN. Additive only.
 *
 * LAW #1 AT RUNTIME. When the monster reaches a tapped thing, this file calls
 * that placeable's `interact()` and fans the returned `InteractionResult` out
 * to whichever crew owns each kind of feedback. Crews never call `interact()`
 * themselves; they subscribe to the events below.
 *
 *   anim      -> 'prop:anim'          (art/interiors crew animates the part)
 *   toggle    -> 'prop:toggle'        (art/interiors crew flips the visual)
 *   monster   -> 'monster:action'     (monster crew plays the action)
 *   particles -> 'fx:play'            (vfx owner spawns the effect)
 *   spawn     -> 'spawn:request'      (whoever owns that `what`)
 *   coins     -> wallet.earnExact + 'economy:earned'
 *   ui        -> 'ui:panel'
 *   sound     -> 'audio:cue'
 *   voice     -> 'audio:voice'
 *   state     -> merged into the instance and autosaved
 *   scene     -> world.transitionTo (a door)
 *
 * Cooldowns are per-instance and exist only to stop a single tap firing twice;
 * they are NEVER a limit on how much a kid may play with something (LAW #2).
 */

import { bus } from '../core/events'
import { wallet } from '../core/economy'
import { engine } from '../core/engine'
import { requirePlaceable, type InteractionContext } from '../core/placeable'
import { save } from '../core/save'
import { world } from '../core/scene'
import type { InstanceId, SceneId, Vec3 } from '../core/types'

/** Live per-instance state + tap bookkeeping. Persisted by the build crew. */
const instanceState = new Map<string, Record<string, unknown>>()
const tapCounts = new Map<string, number>()
const cooldownUntil = new Map<string, number>()

const key = (sceneId: SceneId, id: InstanceId): string => `${sceneId}#${id}`

/** Read an instance's live state (build crew uses this when serialising). */
export function stateOf(sceneId: SceneId, id: InstanceId): Record<string, unknown> | undefined {
  return instanceState.get(key(sceneId, id))
}

/** Seed an instance's state on load / placement. */
export function setStateOf(
  sceneId: SceneId,
  id: InstanceId,
  state: Record<string, unknown>,
): void {
  instanceState.set(key(sceneId, id), state)
}

export function clearStateOf(sceneId: SceneId, id: InstanceId): void {
  instanceState.delete(key(sceneId, id))
  tapCounts.delete(key(sceneId, id))
  cooldownUntil.delete(key(sceneId, id))
}

export function installInteractions(): void {
  bus.on('play:interacted', (e) => {
    run(e.sceneId, e.instanceId)
  })
}

export function run(sceneId: SceneId, instanceId: InstanceId): boolean {
  const node = world.get(sceneId)
  if (node === undefined) return false
  const placement = node.grid.placement(instanceId)
  if (placement === null) return false

  const k = key(sceneId, instanceId)
  const now = engine.now
  const until = cooldownUntil.get(k) ?? 0
  if (now < until) return false

  const def = requirePlaceable(placement.defId)
  let state = instanceState.get(k)
  if (state === undefined) {
    state = def.initialState?.() ?? {}
    instanceState.set(k, state)
  }
  const taps = (tapCounts.get(k) ?? 0) + 1
  tapCounts.set(k, taps)

  const centre = node.grid.footprintCenter(placement.anchor, placement.footprint, placement.rot)
  const ctx: InteractionContext = {
    def,
    instance: {
      id: instanceId,
      defId: placement.defId,
      sceneId,
      anchor: placement.anchor,
      rot: placement.rot,
      state,
    },
    placement,
    sceneId,
    grid: node.grid,
    state,
    art: node.art.get(instanceId) ?? null,
    at: centre,
    taps,
    now,
    rng: engine.rng,
  }

  let result
  try {
    result = def.interact(ctx)
  } catch (err) {
    // A broken prop must never break the game a kid is in the middle of.
    console.error(`[interact] "${def.id}" threw`, err)
    return false
  }

  cooldownUntil.set(k, now + (result.cooldownMs ?? 0))
  let dirty = false

  for (const f of result.feedback) {
    switch (f.kind) {
      case 'anim':
        bus.emit('prop:anim', {
          instanceId,
          clip: f.clip,
          seconds: f.seconds ?? 0.5,
          ...(f.target === undefined ? {} : { target: f.target }),
        })
        break
      case 'toggle':
        bus.emit('prop:toggle', {
          instanceId,
          on: f.on,
          ...(f.target === undefined ? {} : { target: f.target }),
        })
        break
      case 'monster':
        bus.emit('monster:action', { action: f.action, seconds: f.seconds ?? 1 })
        break
      case 'particles':
        bus.emit('fx:play', { effect: f.effect, at: f.at ?? centre, count: f.count ?? 6 })
        break
      case 'state':
        Object.assign(state, f.patch)
        dirty = true
        break
      case 'coins':
        wallet.earnExact(f.amount, f.source, centre)
        dirty = true
        break
      case 'spawn':
        bus.emit('spawn:request', { what: f.what, at: f.at ?? centre })
        break
      case 'ui':
        bus.emit('ui:panel', { panel: f.panel })
        break
      case 'scene':
        void enterScene(f.to)
        break
      case 'sound':
        bus.emit('audio:cue', {
          cue: f.cue,
          at: centre,
          ...(f.volume === undefined ? {} : { volume: f.volume }),
        })
        break
      case 'voice':
        bus.emit('audio:voice', { key: f.key })
        break
    }
  }

  if (dirty) save.request(`interact:${def.id}`)
  return true
}

async function enterScene(to: SceneId): Promise<void> {
  if (!world.has(to)) {
    console.warn(`[interact] door points at unknown scene "${to}"`)
    return
  }
  await world.transitionTo(to)
}

/** Convenience for crews that need the world point of an instance. */
export function instanceCentre(sceneId: SceneId, id: InstanceId): Vec3 | null {
  const node = world.get(sceneId)
  const p = node?.grid.placement(id)
  if (node === undefined || p === null || p === undefined) return null
  return node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
}
